import type { DirectiveBinding } from 'vue';

export const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) {
    return '';
  }
  // Remove any non-digit characters from the phone number
  let value = phoneNumber.replace(/\D+/g, '').slice(0, 10);

  // Format the phone number based on its length
  if (value.length === 10) {
    value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
  } else if (value.length >= 7 && value.length < 10) {
    value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
  } else if (value.length > 3 && value.length < 7) {
    value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
  } else {
    // Return the original input phone number if it's incomplete
    value = phoneNumber.replace(/\D+/g, '').slice(0, 10);
  }
  return value;
};

export const formatZip = (zip: string) => {
  if (!zip) {
    return '';
  }
  // Remove any non-digit characters from the phone number
  let value = zip.replace(/\D+/g, '').slice(0, 9);

  // Format the phone number based on its length
  if (value.length === 5) {
    value = `${value.slice(0, 5)}`;
  } else if (value.length > 5) {
    value = `${value.slice(0, 5)}-${value.slice(5, 9)}`;
  } else {
    // Return the original input phone number if it's incomplete
    value = zip.slice(0, 9);
  }
  return value;
};

export const formatSSN = (ssn: string) => {
  if (!ssn) {
    return '';
  }
  // Remove any non-digit characters from the SSN
  let value = ssn.replace(/\D+/g, '').slice(0, 8);

  // Format the SSN based on its length XXX-XX-XXXX
  if (value.length >= 6) {
    value = `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`;
  } else if (value.length > 3 && value.length < 6) {
    value = `${value.slice(0, 3)}-${value.slice(3)}`;
  } else {
    // Return the original input SSN if it's incomplete
    value = ssn.replace(/\D+/g, '').slice(0, 9);
  }
  return value;
};

export const formatFederalTaxId = (value: string) => {
  value = value.trim();
  if (value.length > 2 && value[2] !== '-') {
    value = `${value.slice(0, 2)}-${value.slice(2)}`;
  }
  return value;
};

export const toPlainSSN = (formattedSSN: string) => {
  // Remove any non-digit characters from the SSN
  return formattedSSN.replace(/\D+/g, '').slice(0, 8);
};

export const toPlainPhone = (phoneNumber: string) => {
  if (!phoneNumber) {
    return '';
  }
  return phoneNumber.replace(/\D+/g, '').slice(0, 10);
};

export const toMaskedSSN = (ssn: string) => {
  if (!ssn) {
    return '-';
  }
  const lastFourDigits = ssn.substring(ssn.length - 4);
  const maskedSSN = '*****' + lastFourDigits;
  return maskedSSN;
};

export const vLiviMask = {
  mounted(el: HTMLElement, binding: DirectiveBinding<string>) {
    const mask = binding.value;

    const input = el.querySelector('input');
    if (input) {
      // Prevent input of non-numeric characters
      input.addEventListener('keydown', (e: KeyboardEvent) => {
        const allowedKeys = [
          'Backspace',
          'Delete',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
          'Home',
          'End',
          'PageUp',
          'PageDown',
          'Control',
          'Meta', // For Mac Command key
        ];

        // Allow number keys
        for (let i = 48; i <= 57; i++) {
          allowedKeys.push(String.fromCharCode(i));
        }

        // Allow special characters for phone formatting
        allowedKeys.push('-', ' ', '(', ')');

        if (!allowedKeys.includes(e.key) && e.key.length === 1) {
          if (mask !== 'federalTaxId') e.preventDefault();
        }
      });

      input.addEventListener('input', (e: Event) => {
        let value = (e.target as HTMLInputElement).value;

        switch (mask) {
          case 'phone':
            value = formatPhoneNumber(value);
            break;
          case 'zip':
            value = formatZip(value);
            break;
          case 'ssn':
            value = formatSSN(value);
            break;
          case 'federalTaxId':
            value = formatFederalTaxId(value);
            break;
          default:
            // set nothing for default
            break;
        }

        (e.target as HTMLInputElement).value = value;
      });
      input.addEventListener('blur', (e: Event) => {
        let value = (e.target as HTMLInputElement).value;

        // Remove non-numeric characters if the mask is 'phone'
        if (mask === 'phone') {
          value = value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
          (e.target as HTMLInputElement).value = formatPhoneNumber(value); // Format the cleaned value
        }

        // Similarly handle other masks if needed
        if (mask === 'zip') {
          value = value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
          (e.target as HTMLInputElement).value = formatZip(value); // Format the cleaned value
        }

        if (mask === 'federalTaxId') {
          (e.target as HTMLInputElement).value = formatFederalTaxId(value);
        }
      });

      // Optionally, format on focus
      input.addEventListener('focus', (e: Event) => {
        let value = (e.target as HTMLInputElement).value;

        if (mask === 'phone') {
          value = value.replace(/[^0-9]/g, ''); // Clean value
          (e.target as HTMLInputElement).value = formatPhoneNumber(value); // Format the cleaned value
        }

        if (mask === 'zip') {
          value = value.replace(/[^0-9]/g, ''); // Clean value
          (e.target as HTMLInputElement).value = formatZip(value); // Format the cleaned value
        }

        if (mask === 'federalTaxId') {
          (e.target as HTMLInputElement).value = formatFederalTaxId(value);
        }
      });
    }
  },
};
